import PropTypes from "prop-types";
import {
    CheckCircleOutlined,
    CreditCardOutlined,
    DeleteOutlined,
    EditOutlined,
    FileAddOutlined,
    PlusOutlined,
    RollbackOutlined,
} from "@ant-design/icons";
import { Button, Empty, Modal, Tabs, Tag, Tooltip } from "antd";
import { useState } from "react";
import apiClient from "../../../../../helpers/apiClient";
import { useToast } from "../../../../../contexts/ToastContext";
import { getStatus } from "../../../../dashboard/components/formatReservations";
import RefundModal2 from "../../modals/RefundModal2";
import ExtraChargeModal from "../../modals/ExtraChargeModal";
import PaymentOptionModal from "../../modals/PaymentOptionModal";
import { normalizePaymentEvents } from "../../../lib/reservationInvoiceHelpers";
import { ACTION_SIZE_SM, SECONDARY_ACTION, getPaymentSummary } from "../utils";
import PaymentEventsTable from "./PaymentEventsTable";

// Due amount once `charge` is removed, before tax recalculation. Negative means money already
// collected would exceed what is owed.
const invoiceDueAfterRemoval = (invoice, charge) =>
    Number(invoice?.due ?? 0) - Number(charge?.total ?? 0);

const PaymentSummaryCard = ({
    resDetails,
    match,
    customerDetails,
    fetchResDetails,
    openRefundModal,
    isRefundModalOpen,
    closeRefundModal,
    openPaymentOptionModal,
    closePaymentOptionModal,
    isPaymentOptionOpen,
    overDueModalOpen,
    overDueModalClose,
    isOpenExtraChargeModal,
    t,
}) => {
    const toast = useToast();
    const [editingCharge, setEditingCharge] = useState(null);
    const [deletingIndex, setDeletingIndex] = useState(null);
    const summary = getPaymentSummary(resDetails);
    const paymentEvents = normalizePaymentEvents(resDetails?.invoice ?? {});
    // Keep each charge's position in the full charges array: charges have no id, and the
    // edit/delete requests rewrite that array.
    const extraCharges = (resDetails?.invoice?.charges || [])
        .map((item, index) => ({ ...item, index }))
        .filter((item) => item.type === "extraCharge");
    // Charges can only be changed while the reservation is live (past ones live in another collection).
    const reservationStatus = getStatus(resDetails || {});
    const canChangeCharges =
        !resDetails?.isRejected && (reservationStatus === "upcoming" || reservationStatus === "ongoing");
    const hasPaymentEvents = paymentEvents.length > 0;
    const hasExtraCharges = extraCharges.length > 0;

    const closeChargeModal = () => {
        setEditingCharge(null);
        overDueModalClose?.();
    };

    const deleteCharge = async (charge) => {
        const invoice = resDetails?.invoice || {};
        const charges = (invoice.charges || []).filter((_, index) => index !== charge.index);

        setDeletingIndex(charge.index);
        try {
            await apiClient.patch(`/api/reservation/${resDetails.id}`, { invoice: { ...invoice, charges } });
            toast.success({ message: t("payment.extraChargeDeleted") });
            await fetchResDetails?.();
        } catch (error) {
            toast.error({
                message: t("payment.failedToDeleteExtraCharge"),
                description: error?.response?.data?.message || error?.message,
            });
            throw error;
        } finally {
            setDeletingIndex(null);
        }
    };

    const confirmDeleteCharge = (charge) => {
        // Taxes are recalculated by the server, so this is only a pre-tax estimate of whether
        // money already collected would exceed the new total.
        const wouldOverpay = Number(invoiceDueAfterRemoval(resDetails?.invoice, charge)) < 0;

        Modal.confirm({
            title: t("payment.deleteExtraChargeTitle"),
            content: (
                <div className="space-y-2">
                    <p className="m-0">
                        {t("payment.deleteExtraChargeDescription", {
                            description: charge.description,
                            amount: Number(charge.total).toFixed(2),
                        })}
                    </p>
                    {wouldOverpay ? (
                        <p className="m-0 text-[#d48806]">{t("payment.deleteExtraChargeOverpaid")}</p>
                    ) : null}
                </div>
            ),
            okText: t("payment.delete"),
            okButtonProps: { danger: true },
            onOk: () => deleteCharge(charge),
        });
    };

    const tabItems = [
        {
            key: "events",
            label: (
                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                    <CreditCardOutlined />
                    {t("payment.paymentEvents")}
                    {hasPaymentEvents ? (
                        <span className="rounded-full bg-[#F2F2FA] px-1.5 py-0.5 text-xs text-[#667085]">
                            {paymentEvents.length}
                        </span>
                    ) : null}
                </span>
            ),
            children: (
                <div className="min-w-0 pt-1">
                    <PaymentEventsTable events={paymentEvents} t={t} />
                </div>
            ),
        },
        {
            key: "charges",
            label: (
                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                    <FileAddOutlined />
                    {t("payment.extraCharges")}
                    {hasExtraCharges ? (
                        <span className="rounded-full bg-[#F2F2FA] px-1.5 py-0.5 text-xs text-[#667085]">
                            {extraCharges.length}
                        </span>
                    ) : null}
                </span>
            ),
            children: (
                <div className="min-w-0 pt-1">
                    {hasExtraCharges ? (
                        <div className="overflow-hidden rounded-xl border border-[#f0f0f0]">
                            <table className="sc-table fontRegular min-w-0">
                                <thead>
                                    <tr className="border-b border-[#f0f0f0] bg-[#FAFAFC]">
                                        <th className="px-4 py-3 text-left text-xs font-medium text-[#7f8c8d]">
                                            {t("payment.description")}
                                        </th>
                                        <th className="w-28 px-4 py-3 text-right text-xs font-medium text-[#7f8c8d]">
                                            {t("payment.amount")}
                                        </th>
                                        {canChangeCharges ? <th className="w-24 px-2 py-3" /> : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {extraCharges.map((item) => (
                                        <tr
                                            key={item.index}
                                            className="border-b border-[#f0f0f0] last:border-b-0">
                                            <td className="px-4 py-3 text-sm text-[#2F2F46]">{item.description}</td>
                                            <td className="px-4 py-3 text-right text-sm font-medium text-[#2F2F46]">
                                                ${Number(item.total).toFixed(2)}
                                            </td>
                                            {canChangeCharges ? (
                                                <td className="px-2 py-3 text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Tooltip title={t("payment.editExtraCharge")}>
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                aria-label={t("payment.editExtraCharge")}
                                                                icon={<EditOutlined />}
                                                                disabled={deletingIndex !== null}
                                                                onClick={() => setEditingCharge(item)}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title={t("payment.delete")}>
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                danger
                                                                aria-label={t("payment.delete")}
                                                                icon={<DeleteOutlined />}
                                                                loading={deletingIndex === item.index}
                                                                disabled={deletingIndex !== null}
                                                                onClick={() => confirmDeleteCharge(item)}
                                                            />
                                                        </Tooltip>
                                                    </div>
                                                </td>
                                            ) : null}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={t("payment.noExtraCharges")}
                            className="py-8"
                        />
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="min-w-0 rounded-2xl border border-[#f0f0f0] bg-white p-4 lg:p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="fontSemi text-base text-[#2F2F46]">{t("upcomingDetails.payment")}</h2>
                <Tag
                    color={summary.hasBalance ? "warning" : "success"}
                    className="!m-0 !rounded-full !px-3 !py-0.5 !text-xs !font-medium">
                    {summary.hasBalance ? t("upcomingDetails.balanceDue") : t("payment.paymentSuccessful")}
                </Tag>
            </div>

            <div className="mb-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-[#E8F7EE] bg-[#F7FFFA] px-4 py-4">
                    <p className="fontRegular text-xs uppercase tracking-[0.08em] text-[#9b9ba5]">
                        {t("upcomingDetails.collected")}
                    </p>
                    <p className="fontSemi mt-2 text-2xl text-[#389e0d]">${summary.collected.toFixed(2)}</p>
                </div>
                <div className="rounded-xl border border-[#FFF1E6] bg-[#FFF9F2] px-4 py-4">
                    <p className="fontRegular text-xs uppercase tracking-[0.08em] text-[#9b9ba5]">
                        {t("upcomingDetails.outstanding")}
                    </p>
                    <p className="fontSemi mt-2 text-2xl text-[#d48806]">${summary.outstanding.toFixed(2)}</p>
                </div>
            </div>

            <Tabs
                defaultActiveKey="events"
                items={tabItems}
                size="middle"
                className="payment-summary-tabs min-w-0 [&_.ant-tabs-nav]:before:border-b-[#f0f0f0] [&_.ant-tabs-nav-list]:flex-nowrap [&_.ant-tabs-nav-wrap]:overflow-x-auto [&_.ant-tabs-tab]:whitespace-nowrap"
            />

            {/* Two equal columns on phones (confirm payment spans both), a left-aligned row otherwise. */}
            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-[#f0f0f0] pt-4 sm:flex sm:flex-wrap">
                <Button
                    icon={<PlusOutlined />}
                    disabled={resDetails?.isRejected}
                    onClick={() => !resDetails?.isRejected && overDueModalOpen()}
                    className={`${ACTION_SIZE_SM} ${SECONDARY_ACTION} w-full sm:w-auto`}>
                    {t("upcomingDetails.addExtraCharge")}
                </Button>
                <Button
                    icon={<RollbackOutlined />}
                    disabled={resDetails?.isRejected}
                    onClick={() => !resDetails?.isRejected && openRefundModal()}
                    className={`${ACTION_SIZE_SM} ${SECONDARY_ACTION} w-full sm:w-auto`}>
                    {t("payment.refund")}
                </Button>
                {resDetails?.isCarSelected && !resDetails?.isPaid && !resDetails?.isRejected ? (
                    <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={openPaymentOptionModal}
                        className={`${ACTION_SIZE_SM} col-span-2 w-full sm:w-auto`}>
                        {t("payment.confirmPayment")}
                    </Button>
                ) : null}
            </div>

            <RefundModal2
                open={isRefundModalOpen && !resDetails?.isRejected}
                onClose={closeRefundModal}
                resDetails={resDetails}
                refetch={fetchResDetails}
            />
            {resDetails?.isCarSelected && !resDetails?.isPaid && !resDetails?.isRejected ? (
                <PaymentOptionModal
                    open={isPaymentOptionOpen}
                    onClose={closePaymentOptionModal}
                    resDetails={resDetails}
                    fetchResDetails={fetchResDetails}
                />
            ) : null}
            <ExtraChargeModal
                match={match}
                overDueModalClose={closeChargeModal}
                customerDetails={customerDetails}
                resDetails={resDetails}
                refetch={fetchResDetails}
                editingCharge={editingCharge}
                isOpen={(isOpenExtraChargeModal || Boolean(editingCharge)) && !resDetails?.isRejected}
            />
        </div>
    );
};

PaymentSummaryCard.propTypes = {
    resDetails: PropTypes.object,
    match: PropTypes.object,
    customerDetails: PropTypes.object,
    fetchResDetails: PropTypes.func,
    openRefundModal: PropTypes.func,
    isRefundModalOpen: PropTypes.bool,
    closeRefundModal: PropTypes.func,
    openPaymentOptionModal: PropTypes.func,
    closePaymentOptionModal: PropTypes.func,
    isPaymentOptionOpen: PropTypes.bool,
    overDueModalOpen: PropTypes.func,
    overDueModalClose: PropTypes.func,
    isOpenExtraChargeModal: PropTypes.bool,
    t: PropTypes.func.isRequired,
};

export default PaymentSummaryCard;
